import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../contexts/languageContext';
import { useTheme } from '../../contexts/themeContext';
import { ChatPersona } from './cvChatClient';
import { useCvChat } from './useCvChat';
import PrivacyNoticeModal from '../privacy/PrivacyNoticeModal';
import businessAvatar from '../../assets/business-cutted.png';
import casualAvatar from '../../assets/casual-cutted.png';

/**
 * Conversazione riusabile, condivisa tra la pagina /talktome e il widget
 * flottante. Non si occupa di posizionamento: lo decide il contenitore.
 *
 * La personalita' della chat e' derivata dal tema del sito:
 * business -> formale, casual -> informale.
 */

export const personaForTheme = (theme: 'business' | 'casual'): ChatPersona =>
  theme === 'business' ? 'formal' : 'informal';

interface CVChatProps {
  /** true quando la conversazione e' visibile: attiva il warmup del backend. */
  active: boolean;
  /** Mostra l'intestazione con titolo e azioni (usata dal widget flottante). */
  showHeader?: boolean;
  onClose?: () => void;
  className?: string;
}

const CVChat: React.FC<CVChatProps> = ({ active, showHeader = false, onClose, className = '' }) => {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const persona = personaForTheme(theme);
  const assistantAvatar = theme === 'business' ? businessAvatar : casualAvatar;
  const backendUnavailableFallbacks =
    persona === 'formal'
      ? [
          t('chatBackendUnavailableFormal1'),
          t('chatBackendUnavailableFormal2'),
          t('chatBackendUnavailableFormal3'),
        ]
      : [
          t('chatBackendUnavailableInformal1'),
          t('chatBackendUnavailableInformal2'),
          t('chatBackendUnavailableInformal3'),
        ];

  const [draft, setDraft] = useState('');
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const {
    messages,
    meta,
    coldStartLevel,
    isBusy,
    cooldownSeconds,
    canSend,
    send,
    stop,
    reset,
    configured,
    simulated,
  } = useCvChat({ active, persona, backendUnavailableFallbacks });

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const contactUrl = meta?.contact_url ?? 'https://www.linkedin.com/in/luca-astolfi-87238745/';
  const maxChars = meta?.max_input_chars ?? 500;

  const suggestions = [t('chatSuggestion1'), t('chatSuggestion2'), t('chatSuggestion3')];

  // Autoscroll a ogni delta di testo.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (active) inputRef.current?.focus();
  }, [active]);

  const submit = useCallback(
    (text: string) => {
      if (!text.trim() || !canSend) return;
      setDraft('');
      void send(text);
    },
    [canSend, send]
  );

  const cooldownLabel = t('chatCooldown').replace('{s}', String(cooldownSeconds));

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit(draft);
    }
  };

  if (!configured) {
    return (
      <div className={`cv-chat__unconfigured ${className}`} role="status">
        <p>{t('chatNotConfigured')}</p>
        <a href={contactUrl} target="_blank" rel="noopener noreferrer">
          {t('chatContact')}
        </a>
      </div>
    );
  }

  return (
    <div className={`cv-chat__conversation ${className}`} data-persona={persona}>
      {showHeader && (
        <header className="cv-chat__header">
          <div>
            <p className="cv-chat__title">{t('chatTitle')}</p>
            <p className="cv-chat__subtitle">
              {persona === 'formal' ? t('chatSubtitleFormal') : t('chatSubtitleInformal')}
            </p>
          </div>
          <div className="cv-chat__header-actions">
            {messages.length > 0 && (
              <button
                type="button"
                className="cv-chat__icon-button"
                onClick={reset}
                aria-label={t('chatReset')}
                title={t('chatReset')}
              >
                <span aria-hidden="true">&#8635;</span>
              </button>
            )}
            {onClose && (
              <button
                type="button"
                className="cv-chat__icon-button"
                onClick={onClose}
                aria-label={t('chatClose')}
                title={t('chatClose')}
              >
                <span aria-hidden="true">&#10005;</span>
              </button>
            )}
          </div>
        </header>
      )}

      {simulated && (
        // Deve essere impossibile confondere una risposta simulata con una reale:
        // il contenuto e' plausibile ma inventato dal transport locale.
        <p className="cv-chat__simulated" role="status">
          {t('chatSimulated')}
        </p>
      )}

      <div className="cv-chat__messages" ref={scrollRef} aria-live="polite" aria-atomic="false">
        {messages.length === 0 && (
          <div className="cv-chat__empty">
            <p className="cv-chat__empty-title">
              {persona === 'formal' ? t('chatIntroFormal') : t('chatIntroInformal')}
            </p>
            <p className="cv-chat__empty-hint">{t('chatHint')}</p>
            <ul className="cv-chat__suggestions">
              {suggestions.map((question) => (
                <li key={question}>
                  <button type="button" onClick={() => submit(question)} disabled={!canSend}>
                    {question}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {messages.map((message) => {
          const isEmptyAssistant = message.role === 'assistant' && !message.content;
          const messageBody = (
            <article
              key={message.id}
              className={[
                'cv-chat__message',
                `cv-chat__message--${message.role}`,
                message.isError ? 'cv-chat__message--error' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {isEmptyAssistant ? (
                <span className="cv-chat__typing" aria-label={t('chatTyping')}>
                  <i />
                  <i />
                  <i />
                  <span className="cv-chat__typing-label">{t('chatTyping')}</span>
                </span>
              ) : (
                message.content
                  .split('\n')
                  .filter((line, index, all) => line.trim() !== '' || all.length === 1)
                  .map((line, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <p key={index}>{line}</p>
                  ))
              )}

              {message.sources && message.sources.length > 0 && (
                <p className="cv-chat__sources">
                  {t('chatSources')}: {message.sources.map((source) => source.id).join(', ')}
                </p>
              )}
            </article>
          );

          if (message.role !== 'assistant') {
            return messageBody;
          }

          return (
            <div key={message.id} className="cv-chat__message-row cv-chat__message-row--assistant">
              <img src={assistantAvatar} alt="Luca Astolfi" className="cv-chat__avatar" />
              {messageBody}
            </div>
          );
        })}

        {coldStartLevel > 0 && (
          <p className="cv-chat__cold-start" role="status">
            {coldStartLevel === 1 ? t('chatConnecting') : t('chatWakingUp')}
          </p>
        )}

        {cooldownSeconds > 0 && !isBusy && (
          <p className="cv-chat__cooldown" role="status">
            <span className="cv-chat__cooldown-timer">{cooldownLabel}</span>
            <span className="cv-chat__cooldown-hint">{t('chatCooldownHint')}</span>
          </p>
        )}
      </div>

      <form
        className="cv-chat__composer"
        onSubmit={(event) => {
          event.preventDefault();
          submit(draft);
        }}
      >
        <textarea
          ref={inputRef}
          className="cv-chat__input"
          value={draft}
          onChange={(event) => setDraft(event.target.value.slice(0, maxChars))}
          onKeyDown={onKeyDown}
          placeholder={
            isBusy
              ? t('chatPlaceholderBusy')
              : cooldownSeconds > 0
                ? cooldownLabel
                : t('chatPlaceholder')
          }
          rows={1}
          maxLength={maxChars}
          disabled={isBusy || cooldownSeconds > 0}
          aria-label={t('chatPlaceholder')}
        />
        {isBusy ? (
          <button
            type="button"
            className="cv-chat__send cv-chat__send--stop"
            onClick={stop}
            aria-label={t('chatStop')}
            title={t('chatStop')}
          >
            <span aria-hidden="true">&#9632;</span>
          </button>
        ) : (
          <button
            type="submit"
            className="cv-chat__send"
            disabled={!draft.trim() || !canSend}
            aria-label={cooldownSeconds > 0 ? cooldownLabel : t('chatSend')}
            title={cooldownSeconds > 0 ? cooldownLabel : t('chatSend')}
          >
            <span aria-hidden="true">{cooldownSeconds > 0 ? cooldownSeconds : '➤'}</span>
          </button>
        )}
      </form>

      <footer className="cv-chat__footer">
        <span>
          {t('chatDisclaimer')}{' '}
          <button type="button" className="cv-chat__link" onClick={() => setPrivacyOpen(true)}>
            {t('privacyLinkLabel')}
          </button>
          {' · '}
          <a href={contactUrl} target="_blank" rel="noopener noreferrer">
            {t('chatContact')}
          </a>
        </span>
        {meta?.budget?.exhausted && <span className="cv-chat__badge">{t('chatQuotaReached')}</span>}
      </footer>

      <PrivacyNoticeModal show={privacyOpen} onHide={() => setPrivacyOpen(false)} />
    </div>
  );
};

export default CVChat;
